import { AssociatedDataSourcesResponse, DataReviewSource } from "../Types/AssociatedDataSources"
import { DataSource } from "../Types/DataSource"
import { RenderStrategy, TraceConfigJSON, TraceDataConfig } from "../Types/Trace"
import { ModalityGraphConfigJSON } from "../Types/Graph"

export type RuntimeDataSources = {
	dataSourceMap: Map<DataSource, number>
	sources: Map<DataSource, DataReviewSource>
	sourceModalities: Map<DataSource, string[]>
}

export function buildCurrentSourceOnlyRegistry(dataObjectId: number, modalityNames: string[], objectType = ""): AssociatedDataSourcesResponse {
	return {
		current_data_object_id: dataObjectId,
		current_source: {
			key: DataSource.CURRENT_PATIENT,
			role: "current",
			relation: "current",
			provider: "active_object",
			data_object_id: dataObjectId,
			object_name: "",
			object_type: objectType,
			label: "Current file",
			read_only: false,
			start_time: null,
			end_time: null,
			time_zone: null,
			modalities: modalityNames.map(name => ({
				name,
				modality: "",
				location: "",
				data_type: "",
				representation: "",
			})),
		},
		sources: [],
		warnings: [
			{
				key: DataSource.ASSOCIATED_OFFLINE_DSA,
				provider: "associated_source_registry",
				reason: "registry_unavailable",
				message: "Associated data sources could not be loaded. Current-file review is still available.",
				expected_object_name: null,
			},
		],
	}
}

export function buildRuntimeDataSources(registry: AssociatedDataSourcesResponse): RuntimeDataSources {
	const registrySources = [registry.current_source, ...registry.sources]
	const sources = new Map<DataSource, DataReviewSource>(registrySources.map(source => [source.key, source]))
	const sourceModalities = new Map<DataSource, string[]>(registrySources.map(source => [source.key, source.modalities.map(modality => modality.name)]))
	const dataSourceMap = new Map<DataSource, number>(registrySources.map(source => [source.key, source.data_object_id]))
	const currentObjectType = registry.current_source.object_type.toUpperCase()
	const associatedCns = sources.get(DataSource.ASSOCIATED_OFFLINE_DSA)
	const timeSeriesSource = currentObjectType === "EDF" ? associatedCns : registry.current_source

	if (timeSeriesSource) {
		dataSourceMap.set(DataSource.REVIEW_TIMESERIES, timeSeriesSource.data_object_id)
		sources.set(DataSource.REVIEW_TIMESERIES, timeSeriesSource)
		sourceModalities.set(
			DataSource.REVIEW_TIMESERIES,
			timeSeriesSource.modalities.map(modality => modality.name),
		)
	} else {
		sourceModalities.set(DataSource.REVIEW_TIMESERIES, [])
	}

	// Preserve the existing placeholder used by on-demand CPPOpt traces.
	dataSourceMap.set(DataSource.CPPOPT_ANALYSIS, 999999)

	return { dataSourceMap, sources, sourceModalities }
}

function traceDataSources(trace: TraceConfigJSON): DataSource[] {
	if (trace.renderStrategy === RenderStrategy.DELTA) {
		return [trace.first.dataSource, trace.second.dataSource]
	}

	return [trace.dataSource]
}

export function getUnavailableDataSources(graphs: ModalityGraphConfigJSON[], dataSourceMap: Map<DataSource, number>): DataSource[] {
	const unavailable = graphs
		.flatMap(graph => graph.traces)
		.flatMap(traceDataSources)
		.filter(source => source !== DataSource.REVIEW_TIMESERIES && !dataSourceMap.has(source))

	return Array.from(new Set(unavailable)).sort()
}

const PORTABLE_RAW_SOURCE_KEYS = new Set<DataSource>([DataSource.CURRENT_PATIENT, DataSource.ASSOCIATED_OFFLINE_DSA, DataSource.REVIEW_TIMESERIES])

function normalizeRawTraceDataSource<T extends TraceDataConfig>(trace: T): T {
	if (trace.onDemandAnalysis !== undefined || !PORTABLE_RAW_SOURCE_KEYS.has(trace.dataSource)) {
		return trace
	}

	return {
		...trace,
		dataSource: DataSource.REVIEW_TIMESERIES,
	} as T
}

function normalizeRawTraceSources(trace: TraceConfigJSON): TraceConfigJSON {
	if (trace.renderStrategy === RenderStrategy.DELTA) {
		return {
			...trace,
			first: normalizeRawTraceDataSource(trace.first),
			second: normalizeRawTraceDataSource(trace.second),
		}
	}

	return normalizeRawTraceDataSource(trace)
}

export function normalizeRawTimeSeriesGraphSources(graphs: ModalityGraphConfigJSON[]): ModalityGraphConfigJSON[] {
	return graphs.map(graph => ({
		...graph,
		traces: graph.traces.map(normalizeRawTraceSources),
	}))
}
